import { CircleCloseIcon, DeleteIcon } from 'assets/icons';
import { CustomButton, CustomModal, DateTimeScheduler, If } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import React, { FC, useEffect, useState } from 'react';
import { VoidFn } from 'types/baseTypes';
import { logAnalyticEvent } from 'utils/analytics';
import { JobsCleverTapEvents } from '../events';

interface EditScheduleModalProps {
  onClose: VoidFn;
  onSave: (scheduledFrom: string, scheduledTo: string) => void;
  onRemove?: VoidFn;
  isScheduled: boolean;
  scheduledFrom: string;
  scheduledTo: string;
  isLoading?: boolean;
  showRemoveOption?: boolean;
}

const EditScheduleModal: FC<EditScheduleModalProps> = ({
  onClose,
  onSave,
  onRemove,
  isScheduled,
  scheduledFrom,
  scheduledTo,
  isLoading = false,
  showRemoveOption = false
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>();
  const [startTime, setStartTime] = useState<Date | null>();
  const [endTime, setEndTime] = useState<Date | null>();
  const [startTimeError, setStartTimeError] = useState('');
  const [endTimeError, setEndTimeError] = useState('');

  useEffect(() => {
    logAnalyticEvent(JobsCleverTapEvents.webJobsSchedulePageView);
  }, []);

  useEffect(() => {
    if (isScheduled) {
      setSelectedDate(new Date(scheduledFrom));
      setStartTime(new Date(scheduledFrom));
      setEndTime(new Date(scheduledTo));
    } else {
      setSelectedDate(new Date());
      setStartTime(new Date());
      setEndTime(new Date(new Date().getTime() + 60 * 60 * 1000));
    }
  }, [isScheduled, scheduledFrom, scheduledTo]);

  const onSaveClick = () => {
    if (startTime && endTime) {
      onSave(startTime.toISOString(), endTime.toISOString());
    }
  };

  const onRemoveClick = () => {
    onRemove?.();
  };

  return (
    <CustomModal
      open={true}
      closeModal={onClose}
      width="md:w-[722px]"
      height="md:h-[256px] md:max-h-[256px]"
      className="overflow-visible">
      <div className="flex grow flex-col px-10">
        <div className="flex items-center justify-between py-6">
          <div className="text-xl font-bold text-headingGray">Schedule job</div>
          <CircleCloseIcon onClick={onClose} className="shrink-0" />
        </div>
        {selectedDate && startTime && endTime && (
          <DateTimeScheduler
            startTimePickerId="edit-job-start-time"
            endTimePickerId="edit-job-end-time"
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            startTime={startTime}
            endTime={endTime}
            setStartTime={setStartTime}
            setEndTime={setEndTime}
            startTimeError={startTimeError}
            setStartTimeError={setStartTimeError}
            endTimeError={endTimeError}
            setEndTimeError={setEndTimeError}
            showPastDates={true}
            timePickerCalendarClassName="w-[150px] left-1.5"
          />
        )}
        <div
          className={`flex flex-row items-center justify-between ${
            startTimeError || endTimeError ? 'mt-[34px]' : 'mt-6'
          }`}>
          <If condition={showRemoveOption && isScheduled}>
            <div
              className="flex cursor-pointer items-center text-17px font-semibold text-error"
              onClick={onRemoveClick}>
              <DeleteIcon className="mr-1 h-[17px] w-[17px]" />
              Remove date
            </div>
          </If>
          <div className={'ml-auto flex'}>
            <CustomButton
              id="edit-job-schedule-cancel-button"
              type={ButtonType.SECONDARY}
              onClick={onClose}
              className="mr-4 w-[100px]">
              Cancel
            </CustomButton>
            <CustomButton
              id="edit-job-schedule-save-button"
              type={ButtonType.PRIMARY}
              disabled={!startTime || !endTime || !!startTimeError || !!endTimeError}
              onClick={onSaveClick}
              className="w-[100px]"
              isLoading={isLoading}>
              Save
            </CustomButton>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default EditScheduleModal;
